import React, { useEffect, useState } from "react";
import {
  IClinicImage,
  IClinicInfo,
  ITreatment,
  IDoctor,
} from "./types/clinic.types";
import { Button, CircularProgress } from "@mui/material";
import PublishIcon from "@mui/icons-material/Publish";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";

// Import Components
import { ClinicImages } from "./components/ClinicImages";
import { ClinicInfo } from "./components/ClinicInfo";
import { AboutUs } from "./components/AboutUs";
import { Treatments } from "./components/Treatments";
import { Doctors } from "./components/Doctors";
import bookUrl from "../../../api/bookUrl";
import { setClinicData } from "../../../redux/bookclinic/bookclinicaction";
 const ClinicManagementPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(true);
  const [images, setImages] = useState<IClinicImage[]>([]);
  const [info, setInfo] = useState<IClinicInfo | null>(null);
  const [about, setAbout] = useState<string>("");
  const [treatments, setTreatments] = useState<ITreatment[]>([]);
  const [doctors, setDoctors] = useState<IDoctor[]>([]);
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);

  useEffect(() => {
    bookUrl
      .get("/bookingclinic/clinic/findBookClinicId")
      .then((response) => {
        const clinicData = response?.data?.data;

        if (clinicData) {
          // Normalize images to ensure they have imageUrl property
          const normalizedImages = (clinicData.images || []).map((img: any) => ({
            ...img,
            imageUrl: img.imageUrl || img.url || img.image || "",
            url: img.url || img.imageUrl || img.image || "",
            id: img.id || img._id || `img_${Date.now()}_${Math.random()}`,
          }));

          // Set all your states from the API response
          setImages(normalizedImages);
          setInfo({
            ...clinicData,
            location: clinicData.location || {
              addressLine: "",
              street: "",
              district: "",
              city: "",
              map: "",
            },
          });
          setAbout(clinicData.about || "");
          setTreatments(clinicData.bookTreatment || []);
          setStatus(clinicData.status || "draft");
          setClinicData(clinicData);
        } else {
          // If no data from API, set empty defaults
          setImages([]);
          setInfo(null);
          setAbout("");
          setTreatments([]);
          setStatus("draft");
        }
      })
      .catch((error) => {
        console.error("Error loading clinic data:", error);
        // Set empty defaults on error
        setImages([]);
        setInfo(null);
        setAbout("");
        setTreatments([]);
        setStatus("draft");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    bookUrl
      .get("/bookingclinic/clinic/findEmployee", {
        headers: { status: true },
      })
      .then((response) => {
        const clinicData = response?.data?.data;
        setDoctors(clinicData || []);
      })
      .catch((error) => {
        console.error("Error loading doctors:", error);
        setDoctors([]);
      })
      .finally(() => {
        setIsLoadingDoctors(false);
      });
  }, []);
  // --- Handlers to update state (passed to children) ---
  const handleImagesChange = (newImages: IClinicImage[]) => {
    setImages(newImages);
  };

  const handleInfoChange = (newInfo: IClinicInfo) => {
    setInfo(newInfo);
  };

  const handleAboutChange = (newAbout: string) => {
    setAbout(newAbout);
  };

  const handleTreatmentsChange = (newTreatments: ITreatment[]) => {
    setTreatments(newTreatments);
  };

  const handleDoctorsChange = (newDoctors: IDoctor[]) => {
    setDoctors(newDoctors);
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const response = await bookUrl.patch("/bookingclinic/clinic/statusBookClinic", {
        status: "published",
      });
      
      if (response.data?.success) {
        setStatus("published");
        toast.success("Clinic published successfully!");
      } else {
        toast.error("Failed to publish clinic");
      }
    } catch (error: any) {
      console.error("Error publishing clinic:", error);
      toast.error(error?.response?.data?.message || "Failed to publish clinic");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDraft = async () => {
    setIsDrafting(true);
    try {
      const response = await bookUrl.patch("/bookingclinic/clinic/statusBookClinic", {
        status: "draft",
      });
      
      if (response.data?.success) {
        setStatus("draft");
        toast.success("Clinic moved to draft successfully!");
      } else {
        toast.error("Failed to move clinic to draft");
      }
    } catch (error: any) {
      console.error("Error moving clinic to draft:", error);
      toast.error(error?.response?.data?.message || "Failed to move clinic to draft");
    } finally {
      setIsDrafting(false);
    }
  };

  const isPublished = status === "published";
  if (isLoading || isLoadingDoctors) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-3"></div>
          <h2 className="text-sm font-semibold text-slate-700">Loading Clinic Data...</h2>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-[1600px] mx-auto px-3 md:px-4 py-3 md:py-4">
      <div className="grid gap-3 grid-cols-1 lg:grid-cols-3">
        {/* 1. Top Area: Clinic Images */}
        <div className="lg:col-span-3">
          <ClinicImages images={images} onImagesChange={handleImagesChange} />
        </div>
        {/* 2. Left Column: Basic Info */}
        <div className="lg:col-span-1">
        <ClinicInfo info={info} onInfoChange={handleInfoChange} />
        </div>
        {/* 3. Right/Main Column */}
        <div className="lg:col-span-2 flex flex-col gap-3">
          {/* 3.1 About Us */}
          <AboutUs aboutContent={about} onAboutChange={handleAboutChange} />
          {/* 3.2 Treatments */}
          <Treatments
            treatments={treatments}
            onTreatmentsChange={handleTreatmentsChange}
          />
          {/* 3.3 Doctors */}
          <Doctors
            doctors={doctors}
            treatments={treatments} // Pass treatments for the multi-select
            onDoctorsChange={handleDoctorsChange}
          />
        </div>
      </div>

      {/* Publish/Draft Button Section */}
      <div className="mt-3 p-3 bg-white rounded-[8px] shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5">
          <div className="flex items-center gap-2">
            {isPublished ? (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-300">
                <CheckCircleIcon sx={{ fontSize: 16 }} />
                <span className="text-xs font-semibold uppercase tracking-wide">Published</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-300">
                <span className="text-xs font-semibold uppercase tracking-wide">Draft</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isPublished ? (
              <Button
                variant="outlined"
                onClick={handleDraft}
                disabled={isDrafting}
                startIcon={
                  isDrafting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <EditIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  borderColor: "#f59e0b",
                  color: "#f59e0b",
                  "&:hover": {
                    borderColor: "#d97706",
                    backgroundColor: "rgba(245, 158, 11, 0.1)",
                  },
              "&:disabled": {
                borderColor: "#9ca3af",
                color: "#9ca3af",
              },
                  minWidth: "180px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  "&:active": {
                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                {isDrafting ? "Moving to Draft..." : "Move to Draft"}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handlePublish}
                disabled={isPublishing}
                startIcon={
                  isPublishing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PublishIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  background: "linear-gradient(90deg, #6366f1 0%, #818cf8 100%)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #818cf8 0%, #6366f1 100%)",
                  },
                  "&:disabled": {
                    background: "#9ca3af",
                    color: "#fff",
                  },
                  minWidth: "200px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  "&:active": {
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                {isPublishing ? "Publishing..." : "Publish Clinic"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default ClinicManagementPage;